import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  findCategoryById,
  CURRENCIES,
  formatPhoneMask,
  normalizeTelegram,
  validatePhone,
  validateTelegram,
  validateWhatsApp,
  formatPrice,
} from '@barter/shared';
import type { AdCondition, Currency } from '@barter/shared';
import { MAX_WANTED_LENGTH } from '@barter/shared';
import WebApp from '@twa-dev/sdk';
import { enqueueCreate } from '../../db/sync-queue';
import { compressImageFile } from '../../services/images';
import { useClassifier } from '../../hooks/useClassifier';
import { PhotoUploader } from '../../components/PhotoUploader/PhotoUploader';
import { CitySelect } from '../../components/CitySelect/CitySelect';
import { CategoryPicker } from '../../components/CategoryPicker/CategoryPicker';
import { useTelegramContext } from '../../context/TelegramContext';

type Step = 'form' | 'preview';

export function CreateAdPage() {
  const navigate = useNavigate();
  const { result: classResult, loading: classLoading, classify } = useClassifier();
  const { user } = useTelegramContext();
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('form');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [negotiable, setNegotiable] = useState(false);
  const [currency, setCurrency] = useState<Currency>('UAH');
  const [categoryId, setCategoryId] = useState('');
  const [categoryManual, setCategoryManual] = useState(false);
  const [condition, setCondition] = useState<AdCondition | ''>('');
  const [wanted, setWanted] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [city, setCity] = useState('');
  const [phone, setPhone] = useState('');
  const [telegram, setTelegram] = useState(user?.username || '');
  const [whatsapp, setWhatsapp] = useState('');
  const [preferred, setPreferred] = useState<'telegram' | 'phone' | 'whatsapp'>(
    user?.username ? 'telegram' : 'phone',
  );
  const [previews, setPreviews] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (title.length >= 3) {
      classify(title, description);
    }
  }, [title, description, classify]);

  useEffect(() => {
    if (classResult && !categoryManual) {
      setCategoryId(classResult.categoryId);
    }
  }, [classResult, categoryManual]);

  const handleCategoryChange = (id: string) => {
    setCategoryId(id);
    setCategoryManual(true);
  };

  const handlePhotosAdd = useCallback((files: File[]) => {
    const newPreviews = files.map((f) => URL.createObjectURL(f));
    setPreviews((prev) => [...prev, ...newPreviews]);
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const handlePhotoRemove = useCallback(
    (index: number) => {
      const url = previews[index];
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      setPreviews((prev) => prev.filter((_, i) => i !== index));
      setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    },
    [previews],
  );

  const handleRequestPhone = useCallback(() => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      WebApp.requestContact((allowed: boolean, contact: any) => {
        if (allowed && contact?.phone_number) {
          setPhone(formatPhoneMask(contact.phone_number));
        }
      });
    } catch {}
  }, []);

  const validate = (): string | null => {
    if (!title.trim() || title.trim().length < 3) return t('createAd.name');
    if (!description.trim() || description.trim().length < 10) return t('createAd.description');
    if (!categoryId) return t('createAd.categoryRequired');
    if (phone) {
      const e = validatePhone(phone);
      if (e) return t(e);
    }
    if (telegram) {
      const e = validateTelegram(telegram);
      if (e) return t(e);
    }
    if (whatsapp) {
      const e = validateWhatsApp(whatsapp);
      if (e) return t(e);
    }
    return null;
  };

  const handlePhoneChange = (value: string) => {
    setPhone(formatPhoneMask(value));
  };

  const handleTelegramChange = (value: string) => {
    setTelegram(normalizeTelegram(value));
  };

  const handlePreview = () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setStep('preview');
  };

  const handleSubmit = async () => {
    if (!user) {
      setError(t('myAds.openInTelegram'));
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      // Фото сжимаем на клиенте (≤1200px WebP/JPEG) и кладём прямо в операцию:
      // объявление создаётся даже офлайн, фото уедут в Worker вместе с очередью.
      const photoDataUrls = await Promise.all(pendingFiles.map((f) => compressImageFile(f)));

      const adData = {
        title: title.trim(),
        description: description.trim(),
        price: negotiable ? null : price ? Number(price) : null,
        currency,
        categoryId,
        condition: condition || undefined,
        wanted: wanted.trim() || undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
        photos: photoDataUrls,
        contacts: {
          phone: phone || undefined,
          telegram: telegram || undefined,
          whatsapp: whatsapp || undefined,
          preferred,
        },
        location: city ? { city } : null,
      };

      const tempAd = {
        id: crypto.randomUUID(),
        title: title.trim(),
        description: description.trim(),
        price: negotiable ? null : price ? Number(price) : null,
        currency,
        categoryId,
        condition: condition || undefined,
        wanted: wanted.trim() || undefined,
        make: make.trim() || undefined,
        model: model.trim() || undefined,
        photos: photoDataUrls,
        contacts: adData.contacts,
        location: city ? { city } : null,
        sellerTelegramId: user?.id ?? 0,
        sellerName: user?.firstName ?? 'User',
        status: 'active' as const,
        views: 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };

      await enqueueCreate(adData, tempAd);
      navigate(`/ad/${tempAd.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
      setStep('form');
    } finally {
      setSubmitting(false);
    }
  };

  const cat = findCategoryById(categoryId);
  const previewPrice = negotiable ? null : price ? Number(price) : null;

  if (step === 'preview') {
    return (
      <div className="create-ad">
        <h2 className="section-title">{t('createAd.previewTitle')}</h2>

        {error && <div className="error-state">{error}</div>}

        <div className="preview-card">
          {previews[0] && (
            <div className="preview-card__photo">
              <img src={previews[0]} alt={title} />
              {previews.length > 1 && (
                <span className="preview-card__photo-count">
                  {t('createAd.photosCount', { count: previews.length })}
                </span>
              )}
            </div>
          )}

          <div className="preview-card__price">
            {formatPrice(previewPrice, currency, t('createAd.negotiable'))}
          </div>
          <h3 className="preview-card__title">{title}</h3>

          <div className="preview-card__meta">
            {cat && (
              <span className="offer-details__category">
                {cat.icon} {t(cat.nameKey ?? cat.id)}
              </span>
            )}
            {condition === 'new' && <span className="badge badge--new">✨ {t('createAd.conditionNew')}</span>}
            {condition === 'used' && <span className="badge badge--used">♻️ {t('createAd.conditionUsed')}</span>}
            {(make.trim() || model.trim()) && (
              <span>
                🚗 {[make.trim(), model.trim()].filter(Boolean).join(' ')}
              </span>
            )}
            {city && <span>📍 {city}</span>}
          </div>

          {wanted.trim() && (
            <div className="preview-card__wanted">
              🔁 <strong>{t('adDetails.wantedTitle')}:</strong> {wanted.trim()}
            </div>
          )}

          <p className="preview-card__description">{description}</p>

          <div className="preview-card__contacts">
            <span className="form-label">{t('createAd.contactsLabel')}</span>
            <div className="preview-card__contact-list">
              {phone && <span>📞 {phone}</span>}
              {telegram && <span>✈️ @{telegram.replace('@', '')}</span>}
              {whatsapp && <span>💬 {whatsapp}</span>}
              <span>
                {t('createAd.prefLabel', {
                  pref:
                    preferred === 'telegram'
                      ? t('createAd.preferredTelegram')
                      : preferred === 'phone'
                        ? t('createAd.preferredPhone')
                        : t('createAd.preferredWhatsapp'),
                })}
              </span>
            </div>
          </div>
        </div>

        <div className="preview-actions">
          <button className="btn btn--full" onClick={() => setStep('form')} disabled={submitting}>
            {t('createAd.edit')}
          </button>
          <button
            className="btn btn--primary btn--full"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? t('createAd.publishing') : t('createAd.publish')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="create-ad">
      <h2 className="section-title">{t('createAd.title')}</h2>

      {error && <div className="error-state">{error}</div>}

      <div className="form-group">
        <label className="form-label">{t('createAd.name')}</label>
        <input
          className="form-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('createAd.namePlaceholder')}
          maxLength={200}
        />
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.description')}</label>
        <textarea
          className="form-textarea"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('createAd.descriptionPlaceholder')}
          maxLength={5000}
          rows={4}
        />
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.category')}</label>
        {classResult && !categoryManual && (
          <div className="ai-hint">
            {t('createAd.aiHint', {
              icon: findCategoryById(classResult.categoryId)?.icon,
              name: findCategoryById(classResult.categoryId)
                ? t(
                    findCategoryById(classResult.categoryId)!.nameKey ??
                      findCategoryById(classResult.categoryId)!.id,
                  )
                : '',
            })}
            {classLoading && ' ...'}
          </div>
        )}
        <CategoryPicker value={categoryId} onChange={handleCategoryChange} />
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.condition')}</label>
        <div className="condition-toggle">
          <button
            type="button"
            className={`condition-toggle__btn${condition === 'new' ? ' condition-toggle__btn--active-new' : ''}`}
            onClick={() => setCondition(condition === 'new' ? '' : 'new')}
          >
            ✨ {t('createAd.conditionNew')}
          </button>
          <button
            type="button"
            className={`condition-toggle__btn${condition === 'used' ? ' condition-toggle__btn--active-used' : ''}`}
            onClick={() => setCondition(condition === 'used' ? '' : 'used')}
          >
            ♻️ {t('createAd.conditionUsed')}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.wanted')}</label>
        <input
          className="form-input"
          type="text"
          value={wanted}
          onChange={(e) => setWanted(e.target.value)}
          placeholder={t('createAd.wantedPlaceholder')}
          maxLength={MAX_WANTED_LENGTH}
        />
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.car')}</label>
        <div className="form-row">
          <input
            className="form-input"
            type="text"
            value={make}
            onChange={(e) => setMake(e.target.value)}
            placeholder={t('createAd.makePlaceholder')}
            maxLength={40}
          />
          <input
            className="form-input"
            type="text"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder={t('createAd.modelPlaceholder')}
            maxLength={60}
          />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.price')}</label>
        <div className="form-row">
          <input
            className="form-input"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0"
            disabled={negotiable}
          />
          <select
            className="form-select form-select--small"
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <label className="form-checkbox">
          <input
            type="checkbox"
            checked={negotiable}
            onChange={(e) => setNegotiable(e.target.checked)}
          />
          {t('createAd.negotiable')}
        </label>
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.city')}</label>
        <CitySelect value={city} onChange={setCity} placeholder={t('createAd.selectCity')} />
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.photos')}</label>
        <PhotoUploader previews={previews} onAdd={handlePhotosAdd} onRemove={handlePhotoRemove} />
      </div>

      <div className="form-group">
        <label className="form-label">{t('createAd.contacts')}</label>
        <div className="form-contact-row">
          <input
            className="form-input"
            type="tel"
            value={phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder={t('createAd.phonePlaceholder')}
          />
          <button type="button" className="btn btn--small" onClick={handleRequestPhone}>
            {t('createAd.getFromTelegram')}
          </button>
        </div>
        <input
          className="form-input"
          type="text"
          value={telegram}
          onChange={(e) => handleTelegramChange(e.target.value)}
          placeholder={t('createAd.telegramPlaceholder')}
        />
        {user?.username && !telegram && (
          <button
            type="button"
            className="btn btn--small btn--full"
            onClick={() => setTelegram(user.username!)}
            style={{ marginTop: 4 }}
          >
            {t('createAd.insertUsername', { username: user.username })}
          </button>
        )}
        <input
          className="form-input"
          type="tel"
          value={whatsapp}
          onChange={(e) => setWhatsapp(formatPhoneMask(e.target.value))}
          placeholder={t('createAd.whatsappPlaceholder')}
        />
        <select
          className="form-select"
          value={preferred}
          onChange={(e) => setPreferred(e.target.value as 'telegram' | 'phone' | 'whatsapp')}
        >
          <option value="telegram">{t('createAd.preferredTelegram')}</option>
          <option value="phone">{t('createAd.preferredPhone')}</option>
          <option value="whatsapp">{t('createAd.preferredWhatsapp')}</option>
        </select>
      </div>

      <button className="btn btn--primary btn--full" onClick={handlePreview} disabled={submitting}>
        {t('createAd.preview')}
      </button>
    </div>
  );
}
